import { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import classNames from 'classnames';
import { useTranslation } from 'hooks';
import { RootState } from 'store';
import apiUser from 'api/user';
import { setQuickActions } from 'actions/home';
import { openModal } from 'actions/modal';
import { fillEmptyActions } from './helpers';
import { QuickActionCard, WrapperBlock } from 'pages/home/components';
import Button from 'components/ui/button';
import { SmallBannerCard } from 'components/SmallBannerCard';
import { v4 as uuid } from 'uuid';

SwiperCore.use([Navigation]);

export const QuickActions: React.FC = memo(() => {
  const dispatch = useDispatch();
  const history = useHistory();
  const getTranslate = useTranslation();
  const { enabled, isLoaded = false } = useSelector(
    (state: RootState) => state.home.quickActions
  );
  const { bannerConfig } = useSelector(
    (state: RootState) => state.settings.homePage
  );

  useEffect(() => {
    if (!isLoaded) {
      fetchQuickActions();
    }
  }, [isLoaded]);

  const fetchQuickActions = async () => {
    try {
      const { quickActions } = await apiUser.getConfig({
        param: 'quickActions',
      });
      dispatch(setQuickActions({ quickActions }));
    } catch (err) {
      console.error(err);
    }
  };

  const enabledActions = useMemo(() => {
    const actions = fillEmptyActions(enabled).map((item) => {
      if (!item) {
        return {
          id: `new-${uuid()}`,
          icon: 'im-Plus',
          title: 'getStarted.quickActions.add.label',
          url: '',
        };
      }

      return item;
    });

    if (window.innerWidth < 768) {
      return actions.filter(({ id }) => id);
    }

    return actions;
  }, [enabled]);

  const handleOpenModal = () => {
    dispatch(
      openModal({
        modalId: 'QuickActionsSetting',
        callback: (result) => {
          if (result) {
            dispatch(setQuickActions({ quickActions: result }));
          }
        },
      })
    );
  };

  return (
    <WrapperBlock
      title={getTranslate('getStarted.quickActions.header')}
      actions={
        <Button
          status='light'
          text={getTranslate('common.edit.button')}
          icon='im-Configurator'
          iconPosition='left'
          iconSize={14}
          customClass='tablet:inline-flex hidden'
          onClick={handleOpenModal}
        />
      }>
      <div
        className={classNames(
          'max-desktop-s:w-auto max-desktop-s:block inline-block w-[calc(50%-8px)]',
          {
            'w-full': bannerConfig === null,
          }
        )}>
        <Swiper
          className='max-tablet:overflow-visible'
          enabled={window.innerWidth <= 768}
          breakpoints={{
            320: {
              slidesPerView: 2.1,
              spaceBetween: 8,
            },
            560: {
              slidesPerView: 3.5,
              spaceBetween: 8,
            },
            769: {
              slidesPerView: 4,
              spaceBetween: 16,
            },
          }}>
          {isLoaded
            ? enabledActions.map(({ id, icon, title, url }) => (
              <SwiperSlide key={id}>
                <QuickActionCard
                  isNew={!url}
                  iconName={icon}
                  title={getTranslate(title)}
                  isExpanded={bannerConfig === null}
                  onClick={!url ? handleOpenModal : () => history.push(url)}
                />
              </SwiperSlide>
            ))
            : [1, 2, 3, 4].map((item) => (
              <SwiperSlide key={item}>
                <QuickActionCard
                  isLoading
                  isExpanded={bannerConfig === null}
                />
              </SwiperSlide>
            ))}
        </Swiper>
      </div>

      {bannerConfig !== null && <SmallBannerCard {...bannerConfig} />}
    </WrapperBlock>
  );
});
